import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Popover, 
  Paper, 
  Typography, 
  Box, 
  IconButton, 
  Button,
  Divider,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import useLanguageStore from '@stores/languageStore';
import useCartStore, { SERVICE_TYPES } from '@stores/cartStore';
import obituaryIcon from '@static/obituary-icon.png';
import tombstoneIcon from '@static/tombstone-icon.png';
import refurbishIcon from '@static/refurbish-icon.png';
import rabiIcon from '@static/rabi-icon.png';

const CartPopover = ({ anchorEl, open, onClose }) => {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const isRtl = language === 'he';
  const navigate = useNavigate();
  
  const { items, removeItem, updateItemQuantity, clearCart } = useCartStore();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  
  const getServiceName = (serviceType) => {
    switch (serviceType) {
      case SERVICE_TYPES.OBITUARY:
        return t('home.services.obituary.title');
      case SERVICE_TYPES.TOMBSTONE:
        return t('home.services.tombstone.title');
      case SERVICE_TYPES.SCHEDULE_RABBI:
        return t('home.services.scheduleRabi.title');
      case SERVICE_TYPES.REFURBISH:
        return t('home.services.refurbish.title');
      default:
        return serviceType;
    }
  };
  
  const getServiceRoute = (serviceType) => {
    switch (serviceType) {
      case SERVICE_TYPES.OBITUARY:
        return '/obituary';
      case SERVICE_TYPES.TOMBSTONE:
        return '/tombstone';
      case SERVICE_TYPES.SCHEDULE_RABBI:
        return '/schedule-rabbi';
      default:
        return '/services';
    }
  };
  
  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case SERVICE_TYPES.OBITUARY:
        return obituaryIcon;
      case SERVICE_TYPES.TOMBSTONE:
        return tombstoneIcon;
      case SERVICE_TYPES.SCHEDULE_RABBI:
        return rabiIcon;
      case SERVICE_TYPES.REFURBISH:
        return refurbishIcon;
      default:
        return null;
    }
  };
  
  const getColorLabel = (colorValue) => {
    if (!colorValue) return '';
    if (colorValue === 'gray') return t('tombstone.gray');
    if (colorValue === '#323131' || colorValue === 'black') return t('tombstone.black');
    if (colorValue === 'natural') return t('tombstone.natural');
    return colorValue;
  };

  const formatItemData = (item) => {
    if (item.serviceType === SERVICE_TYPES.OBITUARY) {
      return item.data.name ? `${t('cart.item.deceasedName')}: ${item.data.name}` : '';
    } else if (item.serviceType === SERVICE_TYPES.SCHEDULE_RABBI) {
      const parts = [];
      if (item.data.firstName) parts.push(`${t('cart.item.firstName')}: ${item.data.firstName}`);
      if (item.data.date) parts.push(`${t('scheduleRabi.form.date')}: ${item.data.date}`);
      if (item.data.time) parts.push(`${t('scheduleRabi.form.time')}: ${item.data.time}`);
      return parts.join(', ');
    } else if (item.serviceType === SERVICE_TYPES.TOMBSTONE) {
      const colors = [];
      if (item.data.pillowColor) {
        colors.push(`${t('tombstone.pillow')}: ${getColorLabel(item.data.pillowColor)}`);
      }
      if (item.data.mainStoneColor) {
        colors.push(`${t('tombstone.mainStone')}: ${getColorLabel(item.data.mainStoneColor)}`);
      }
      if (item.data.baseColor) {
        colors.push(`${t('tombstone.base')}: ${getColorLabel(item.data.baseColor)}`);
      }
      return colors.join(' • ');
    }
    return '';
  };
  
  const handleViewCart = () => {
    onClose();
    navigate('/cart');
  };
  
  const handleProceedToOrder = () => {
    onClose();
    navigate('/order');
  };
  
  const handleRemoveClick = (itemId) => {
    setItemToRemove(itemId);
    setRemoveDialogOpen(true);
  };
  
  const handleConfirmRemove = () => {
    if (itemToRemove) {
      removeItem(itemToRemove);
      setItemToRemove(null);
    }
    setRemoveDialogOpen(false);
  };
  
  const handleCancelRemove = () => {
    setItemToRemove(null);
    setRemoveDialogOpen(false);
  };
  
  const handleQuantityDecrease = (itemId, currentQuantity) => {
    if (currentQuantity <= 1) {
      // Quantity will become 0, show confirmation
      setItemToRemove(itemId);
      setRemoveDialogOpen(true);
    } else {
      updateItemQuantity(itemId, currentQuantity - 1);
    }
  };
  
  const handleEditClick = (itemId, serviceType) => {
    onClose();
    navigate(`${getServiceRoute(serviceType)}?edit=${itemId}`);
  };
  
  const handleClearCartClick = () => {
    setClearDialogOpen(true);
  };
  
  const handleConfirmClearCart = () => {
    clearCart();
    setClearDialogOpen(false);
  };
  
  const handleCancelClearCart = () => {
    setClearDialogOpen(false);
  };
  
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: isRtl ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: isRtl ? 'left' : 'right',
      }}
      PaperProps={{
        style: {
          width: '400px',
          maxHeight: '600px',
          marginTop: '8px',
        },
      }}
    >
      <Paper className="p-4 bg-white">
        <Box className="flex items-center justify-between mb-4">
          <Typography variant="h6" className="font-bold text-stone-800">
            {t('cart.title')}
          </Typography>
          {items.length > 0 && (
            <Button
              size="small"
              onClick={handleClearCartClick}
              className="normal-case text-xs text-stone-600 hover:text-stone-800"
            >
              {t('cart.summary.clearCart')}
            </Button>
          )}
        </Box>
        
        {items.length === 0 ? (
          <Box className="text-center py-8">
            <ShoppingCartIcon className="text-stone-300 mb-4" style={{ fontSize: 48 }} />
            <Typography variant="body2" className="text-stone-500 mb-4">
              {t('cart.empty.description')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                onClose();
                navigate('/services');
              }}
              className="normal-case text-stone-600 border-stone-300"
            >
              {t('cart.empty.browseServices')}
            </Button>
          </Box>
        ) : (
          <>
            <Box className="max-h-[400px] overflow-y-auto mb-4">
              <List dense>
                {items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem className="px-0">
                      <Box className="flex items-start gap-3 flex-1">
                        <img 
                          src={getServiceIcon(item.serviceType)} 
                          alt={getServiceName(item.serviceType)}
                          className="w-10 h-10 object-contain flex-shrink-0"
                        />
                        <Box className="flex-1 min-w-0">
                          <Typography variant="body2" className="font-semibold text-stone-800 mb-1.5">
                            {getServiceName(item.serviceType)}
                          </Typography>
                        {item.serviceType === SERVICE_TYPES.TOMBSTONE && (
                          <Box className="mb-2 space-y-0.5">
                            {item.data.pillowColor && (
                              <Typography variant="caption" className="text-stone-600 block">
                                <span className="font-medium text-stone-700">{t('tombstone.pillow')}:</span> {getColorLabel(item.data.pillowColor)}
                              </Typography>
                            )}
                            {item.data.mainStoneColor && (
                              <Typography variant="caption" className="text-stone-600 block">
                                <span className="font-medium text-stone-700">{t('tombstone.mainStone')}:</span> {getColorLabel(item.data.mainStoneColor)}
                              </Typography>
                            )}
                            {item.data.baseColor && (
                              <Typography variant="caption" className="text-stone-600 block">
                                <span className="font-medium text-stone-700">{t('tombstone.base')}:</span> {getColorLabel(item.data.baseColor)}
                              </Typography>
                            )}
                          </Box>
                        )}
                          {item.serviceType !== SERVICE_TYPES.TOMBSTONE && formatItemData(item) && (
                            <Typography variant="caption" className="text-stone-600 block mb-2">
                              {formatItemData(item)}
                            </Typography>
                          )}
                          <Box className="mb-2">
                            <Button
                              size="small"
                              onClick={() => handleEditClick(item.id, item.serviceType)}
                              className="normal-case text-xs text-stone-600 hover:text-stone-800 p-0 min-w-0"
                              style={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              {t('cart.item.edit')}
                            </Button>
                          </Box>
                          <Box className={`flex items-center gap-2 ${isRtl ? 'justify-end' : 'justify-start'}`}>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityDecrease(item.id, item.quantity)}
                              className="text-stone-600 hover:text-stone-800"
                              style={{ padding: '4px' }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" className="font-semibold text-stone-800 min-w-[2rem] text-center">
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                              className="text-stone-600 hover:text-stone-800"
                              style={{ padding: '4px' }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveClick(item.id)}
                              className="text-red-500 hover:text-red-700 ml-2"
                              style={{ padding: '4px' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < items.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
            
            <Divider className="mb-4" />
            
            <Box className="space-y-2">
              <Typography variant="body2" className="text-stone-600 mb-3 text-center">
                {t('cart.summary.items', { count: items.reduce((sum, item) => sum + item.quantity, 0) })}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={handleProceedToOrder}
                className="mb-2 py-2 normal-case font-semibold bg-stone-700 hover:bg-stone-600"
              >
                {t('cart.summary.proceedToOrder')}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleViewCart}
                className="normal-case text-stone-600 border-stone-300 hover:border-stone-400"
              >
                {t('cart.viewFullCart')}
              </Button>
            </Box>
          </>
        )}
      </Paper>
      
      {/* Remove Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleCancelRemove}
        aria-labelledby="remove-dialog-title"
        aria-describedby="remove-dialog-description"
      >
        <DialogTitle id="remove-dialog-title" className={isRtl ? 'text-right' : 'text-left'}>
          {t('cart.removeConfirm.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-dialog-description" className={isRtl ? 'text-right' : 'text-left'}>
            {t('cart.removeConfirm.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={isRtl ? 'flex-row-reverse' : ''}>
          <Button onClick={handleCancelRemove} className="normal-case text-stone-600">
            {t('cart.removeConfirm.cancel')}
          </Button>
          <Button onClick={handleConfirmRemove} color="secondary" variant="contained" className="normal-case bg-red-600 hover:bg-red-700">
            {t('cart.removeConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Clear Cart Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={handleCancelClearCart}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
      >
        <DialogTitle id="clear-dialog-title" className={isRtl ? 'text-right' : 'text-left'}>
          {t('cart.clearConfirm.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-dialog-description" className={isRtl ? 'text-right' : 'text-left'}>
            {t('cart.clearConfirm.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={isRtl ? 'flex-row-reverse' : ''}>
          <Button onClick={handleCancelClearCart} className="normal-case text-stone-600">
            {t('cart.clearConfirm.cancel')}
          </Button>
          <Button onClick={handleConfirmClearCart} color="secondary" variant="contained" className="normal-case bg-red-600 hover:bg-red-700">
            {t('cart.clearConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Popover>
  );
};

export default CartPopover;

